import { gql, useMutation, useQuery } from '@apollo/client';
import { BarChart, CompositeChart, DonutChart } from '@mantine/charts';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  ColorSwatch,
  Container,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useLocalStorage } from '@mantine/hooks';
import { IconChevronLeft, IconChevronRight, IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { formatCurrency } from '@/utils/format';

const DAYS_PER_MONTH = 30.4375;
const PALETTE = ['blue.6', 'teal.6', 'grape.6', 'orange.6', 'cyan.6', 'lime.6', 'pink.6', 'indigo.6'];
const swatch = (c: string) => `var(--mantine-color-${c.replace('.', '-')})`;

const fmtNum = (n: number) => new Intl.NumberFormat('sk-SK').format(Math.round(n || 0));
const fmtPct = (n: number) => `${(n || 0).toFixed(1)} %`;

type Mode = 'week' | 'month' | 'custom';
type ExpenseType = 'REGULAR' | 'ONE_OFF';

interface ExpenseRow {
  id: string;
  name: string;
  amount: number;
  type: ExpenseType;
  date: Date | null; // apollo-link-scalars deserializes DateTime to a Date
}

interface PaymentBreakdown {
  method: string;
  orderCount: number;
  total: number;
  paidTotal: number;
  unpaidTotal: number;
  paidOrderCount: number;
  unpaidOrderCount: number;
}

interface RevenueStats {
  productRevenue: number;
  shippingRevenue: number;
  paymentRevenue: number;
  customRevenue: number;
  grossInflow: number;
  refundsTotal: number;
  orderCount: number;
  unitsSold: number;
  uniqueCustomerCount: number;
  newCustomerCount: number;
  paidTotal: number;
  unpaidTotal: number;
  paidOrderCount: number;
  unpaidOrderCount: number;
  byPaymentMethod: PaymentBreakdown[];
  byLive: {
    liveId: string;
    liveName: string;
    liveDate: Date | null;
    total: number;
    productRevenue: number;
    orderCount: number;
  }[];
  topProducts: { productId: string; productName: string; total: number; units: number }[];
  days: { date: string; total: number; productRevenue: number; paidTotal: number }[];
}

interface RevenueStatsData {
  revenueStatistics: RevenueStats;
}
interface ExpensesData {
  expenses: ExpenseRow[];
}
interface SettingData {
  setting: { key: string; value: string } | null;
}

const REVENUE_STATISTICS = gql`
  query RevenueStatistics($from: DateTime!, $to: DateTime!) {
    revenueStatistics(from: $from, to: $to) {
      productRevenue
      shippingRevenue
      paymentRevenue
      customRevenue
      grossInflow
      refundsTotal
      orderCount
      unitsSold
      uniqueCustomerCount
      newCustomerCount
      paidTotal
      unpaidTotal
      paidOrderCount
      unpaidOrderCount
      byPaymentMethod {
        method
        orderCount
        total
        paidTotal
        unpaidTotal
        paidOrderCount
        unpaidOrderCount
      }
      byLive {
        liveId
        liveName
        liveDate
        total
        productRevenue
        orderCount
      }
      topProducts {
        productId
        productName
        total
        units
      }
      days {
        date
        total
        productRevenue
        paidTotal
      }
    }
  }
`;

const EXPENSES = gql`
  query Expenses {
    expenses {
      id
      name
      amount
      type
      date
    }
  }
`;
const CREATE_EXPENSE = gql`
  mutation CreateExpense($name: String!, $amount: Int!, $type: ExpenseType!, $date: DateTime) {
    createExpense(name: $name, amount: $amount, type: $type, date: $date) {
      id
      name
      amount
      type
      date
    }
  }
`;
const UPDATE_EXPENSE = gql`
  mutation UpdateExpense($id: String!, $name: String, $amount: Int, $date: DateTime) {
    updateExpense(id: $id, name: $name, amount: $amount, date: $date) {
      id
      name
      amount
      type
      date
    }
  }
`;
const DELETE_EXPENSE = gql`
  mutation DeleteExpense($id: String!) {
    deleteExpense(id: $id) {
      id
    }
  }
`;
const SETTING = gql`
  query Setting($key: String!) {
    setting(key: $key) {
      key
      value
    }
  }
`;
const UPSERT_SETTING = gql`
  mutation UpsertSetting($key: String!, $value: String!) {
    upsertSetting(key: $key, value: $value) {
      key
      value
    }
  }
`;

const toDateValue = (d: Date | null) =>
  d ? dayjs(d).startOf('day').add(12, 'hour').toDate() : null;

// --- financial derivation for one period ---
interface Fin {
  grossInflow: number;
  productRevenue: number;
  refundsTotal: number;
  costOfGoods: number;
  regularTotal: number;
  oneOffTotal: number;
  grossProfit: number;
  netProfit: number;
}
function computeFin(
  stats: RevenueStats | undefined,
  fromStr: string,
  toStr: string,
  rangeDays: number,
  margin: number,
  regularMonthly: number,
  oneOff: ExpenseRow[]
): Fin {
  const grossInflow = stats?.grossInflow ?? 0;
  const productRevenue = stats?.productRevenue ?? 0;
  const refundsTotal = stats?.refundsTotal ?? 0;
  const costOfGoods = productRevenue / (1 + margin / 100);
  const regularTotal = (regularMonthly / DAYS_PER_MONTH) * rangeDays;
  const oneOffTotal = oneOff.reduce((s, e) => {
    if (!e.date) return s;
    const ds = dayjs(e.date).format('YYYY-MM-DD');
    return ds >= fromStr && ds <= toStr ? s + (e.amount || 0) : s;
  }, 0);
  const grossProfit = grossInflow - costOfGoods - refundsTotal;
  const netProfit = grossProfit - regularTotal - oneOffTotal;
  return {
    grossInflow,
    productRevenue,
    refundsTotal,
    costOfGoods,
    regularTotal,
    oneOffTotal,
    grossProfit,
    netProfit,
  };
}

const pctDelta = (cur: number, prev: number | null): number | null => {
  if (prev == null) return null;
  if (prev === 0) return cur === 0 ? 0 : null;
  return ((cur - prev) / Math.abs(prev)) * 100;
};

function Kpi({
  label,
  value,
  prev,
  fmt = formatCurrency,
  goodUp = true,
  emphasis = false,
  valueColor,
  subtitle,
}: {
  label: string;
  value: number;
  prev: number | null;
  fmt?: (n: number) => string;
  goodUp?: boolean;
  emphasis?: boolean;
  valueColor?: string;
  subtitle?: string;
}) {
  const d = pctDelta(value, prev);
  let deltaColor = 'dimmed';
  if (d != null && Math.abs(d) >= 0.5) deltaColor = d > 0 === goodUp ? 'teal' : 'red';
  return (
    <Card withBorder radius="md" padding="md">
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text fw={700} size={emphasis ? 'xl' : 'lg'} c={valueColor}>
        {fmt(value)}
      </Text>
      <Group gap={6} mt={2} wrap="nowrap">
        {d != null ? (
          <Text size="xs" c={deltaColor} fw={600}>
            {d >= 0 ? '▲' : '▼'} {Math.abs(d).toFixed(0)} %
          </Text>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
        {subtitle && (
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        )}
      </Group>
    </Card>
  );
}

export function NetProfitPage() {
  const [mode, setMode] = useLocalStorage<Mode>({ key: 'profit-mode', defaultValue: 'month' });
  const [anchorStr, setAnchorStr] = useLocalStorage<string>({
    key: 'profit-anchor',
    defaultValue: dayjs().format('YYYY-MM-DD'),
  });
  const [rangeStr, setRangeStr] = useLocalStorage<[string | null, string | null]>({
    key: 'profit-range',
    defaultValue: [null, null],
  });

  const { from, to, prevFrom, prevTo } = useMemo(() => {
    const anchor = dayjs(anchorStr);
    if (mode === 'week') {
      const f = anchor.startOf('week');
      const t = anchor.endOf('week');
      return { from: f, to: t, prevFrom: f.subtract(1, 'week'), prevTo: f.subtract(1, 'week').endOf('week') };
    }
    if (mode === 'custom') {
      const f = rangeStr[0] ? dayjs(rangeStr[0]).startOf('day') : anchor.startOf('month');
      const t = rangeStr[1] ? dayjs(rangeStr[1]).endOf('day') : anchor.endOf('month');
      const len = Math.max(1, t.diff(f, 'day') + 1);
      const pt = f.subtract(1, 'day').endOf('day');
      return { from: f, to: t, prevFrom: pt.startOf('day').subtract(len - 1, 'day'), prevTo: pt };
    }
    const f = anchor.startOf('month');
    const t = anchor.endOf('month');
    return { from: f, to: t, prevFrom: f.subtract(1, 'month'), prevTo: f.subtract(1, 'month').endOf('month') };
  }, [mode, anchorStr, rangeStr]);

  const rangeDays = Math.max(1, to.diff(from, 'day') + 1);
  const prevRangeDays = Math.max(1, prevTo.diff(prevFrom, 'day') + 1);
  const fromStr = from.format('YYYY-MM-DD');
  const toStr = to.format('YYYY-MM-DD');
  const prevFromStr = prevFrom.format('YYYY-MM-DD');
  const prevToStr = prevTo.format('YYYY-MM-DD');

  const step = (dir: number) => {
    if (mode === 'custom') {
      const len = rangeDays;
      setRangeStr([
        from.add(dir * len, 'day').format('YYYY-MM-DD'),
        to.add(dir * len, 'day').format('YYYY-MM-DD'),
      ]);
    } else {
      setAnchorStr(dayjs(anchorStr).add(dir, mode).format('YYYY-MM-DD'));
    }
  };

  const { data: statsData, loading: statsLoading } = useQuery<RevenueStatsData>(REVENUE_STATISTICS, {
    variables: { from: from.toDate(), to: to.toDate() },
  });
  const { data: prevData } = useQuery<RevenueStatsData>(REVENUE_STATISTICS, {
    variables: { from: prevFrom.toDate(), to: prevTo.toDate() },
  });
  const stats = statsData?.revenueStatistics;
  const prevStats = prevData?.revenueStatistics;

  // Margin (markup), persisted in DB
  const { data: marginData } = useQuery<SettingData>(SETTING, { variables: { key: 'profitMargin' } });
  const [margin, setMargin] = useState<number>(100);
  useEffect(() => {
    const v = marginData?.setting?.value;
    if (v != null) setMargin(parseFloat(v) || 0);
  }, [marginData]);
  const [upsertSetting] = useMutation(UPSERT_SETTING);
  const saveMargin = () => upsertSetting({ variables: { key: 'profitMargin', value: String(margin) } });

  // Expenses
  const { data: expData, refetch: refetchExpenses } = useQuery<ExpensesData>(EXPENSES);
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  useEffect(() => {
    if (expData?.expenses) setExpenses(expData.expenses);
  }, [expData]);
  const [createExpense] = useMutation(CREATE_EXPENSE, { onCompleted: () => refetchExpenses() });
  const [updateExpense] = useMutation(UPDATE_EXPENSE);
  const [deleteExpense] = useMutation(DELETE_EXPENSE, { onCompleted: () => refetchExpenses() });
  const patchLocal = (id: string, patch: Partial<ExpenseRow>) =>
    setExpenses((cur) => cur.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  const regular = expenses.filter((e) => e.type === 'REGULAR');
  const oneOff = expenses.filter((e) => e.type === 'ONE_OFF');
  const regularMonthly = regular.reduce((s, e) => s + (e.amount || 0), 0);

  const fin = computeFin(stats, fromStr, toStr, rangeDays, margin, regularMonthly, oneOff);
  const prevFin = computeFin(prevStats, prevFromStr, prevToStr, prevRangeDays, margin, regularMonthly, oneOff);

  // KPI helpers
  const aov = stats && stats.orderCount > 0 ? fin.grossInflow / stats.orderCount : 0;
  const prevAov = prevStats && prevStats.orderCount > 0 ? prevFin.grossInflow / prevStats.orderCount : 0;
  const marginPct = fin.grossInflow > 0 ? (fin.netProfit / fin.grossInflow) * 100 : 0;
  const prevMarginPct = prevFin.grossInflow > 0 ? (prevFin.netProfit / prevFin.grossInflow) * 100 : 0;
  const expensesTotal = fin.regularTotal + fin.oneOffTotal;
  const prevExpensesTotal = prevFin.regularTotal + prevFin.oneOffTotal;

  const compareLabel =
    mode === 'week' ? 'pred. týždeň' : mode === 'month' ? 'pred. mesiac' : 'pred. obdobie';

  // Chart data
  // Signed breakdown: revenue/profit up (green), deductions down below 0 (red/orange).
  const breakdownData = [
    { step: 'Tržby', value: fin.grossInflow, color: 'teal.6' },
    { step: 'Tovar', value: -fin.costOfGoods, color: 'red.5' },
    { step: 'Vrátené', value: -fin.refundsTotal, color: 'red.5' },
    { step: 'Pravidelné', value: -fin.regularTotal, color: 'orange.5' },
    { step: 'Vlastné', value: -fin.oneOffTotal, color: 'orange.5' },
    { step: 'Čistý zisk', value: fin.netProfit, color: fin.netProfit >= 0 ? 'teal.7' : 'red.7' },
  ];
  const trendData = (stats?.days ?? []).map((d) => ({
    date: d.date.slice(5),
    Tržby: d.total,
    Zisk: Math.round(d.total - d.productRevenue / (1 + margin / 100)),
  }));
  const donutData = (stats?.byPaymentMethod ?? []).map((p, i) => ({
    name: p.method,
    value: p.total,
    color: PALETTE[i % PALETTE.length],
  }));

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" align="flex-end" mb="md" wrap="wrap">
        <Stack gap={2}>
          <Title order={2}>Čistý zisk</Title>
          <Text size="sm" c="dimmed">
            {from.format('DD.MM.YYYY')} – {to.format('DD.MM.YYYY')} · {rangeDays} dní ·{' '}
            {stats?.orderCount ?? 0} obj. · vs {compareLabel}
          </Text>
        </Stack>
        <Group align="flex-end" gap="xs" wrap="wrap">
          <ActionIcon variant="default" size="lg" onClick={() => step(-1)} aria-label="Predošlé">
            <IconChevronLeft size={18} />
          </ActionIcon>
          <SegmentedControl
            value={mode}
            onChange={(v) => setMode(v as Mode)}
            data={[
              { label: 'Týždeň', value: 'week' },
              { label: 'Mesiac', value: 'month' },
              { label: 'Vlastné', value: 'custom' },
            ]}
          />
          <ActionIcon variant="default" size="lg" onClick={() => step(1)} aria-label="Ďalšie">
            <IconChevronRight size={18} />
          </ActionIcon>
          <Button variant="default" onClick={() => setAnchorStr(dayjs().format('YYYY-MM-DD'))}>
            Dnes
          </Button>
          {mode === 'custom' && (
            <DatePickerInput
              type="range"
              value={[rangeStr[0] ? new Date(rangeStr[0]) : null, rangeStr[1] ? new Date(rangeStr[1]) : null]}
              onChange={(r) =>
                setRangeStr([
                  r[0] ? dayjs(r[0]).format('YYYY-MM-DD') : null,
                  r[1] ? dayjs(r[1]).format('YYYY-MM-DD') : null,
                ])
              }
              valueFormat="DD.MM.YYYY"
              w={230}
            />
          )}
          <NumberInput
            label="Marža (prirážka %)"
            value={margin}
            onChange={(v) => setMargin(typeof v === 'number' ? v : parseFloat(String(v)) || 0)}
            onBlur={saveMargin}
            min={0}
            suffix=" %"
            w={140}
          />
        </Group>
      </Group>

      {statsLoading && !stats ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : (
        <Stack gap="md">
          {/* KPI cards */}
          <SimpleGrid cols={{ base: 2, sm: 3, lg: 6 }}>
            <Kpi
              label="Čistý zisk"
              value={fin.netProfit}
              prev={prevStats ? prevFin.netProfit : null}
              emphasis
              valueColor={fin.netProfit >= 0 ? 'teal' : 'red'}
            />
            <Kpi label="Tržby" value={fin.grossInflow} prev={prevStats ? prevFin.grossInflow : null} />
            <Kpi label="Hrubý zisk" value={fin.grossProfit} prev={prevStats ? prevFin.grossProfit : null} />
            <Kpi
              label="Čistá marža"
              value={marginPct}
              prev={prevStats ? prevMarginPct : null}
              fmt={fmtPct}
            />
            <Kpi
              label="Objednávky"
              value={stats?.orderCount ?? 0}
              prev={prevStats ? prevStats.orderCount : null}
              fmt={fmtNum}
            />
            <Kpi label="Ø objednávka" value={aov} prev={prevStats ? prevAov : null} />
            <Kpi
              label="Kusy"
              value={stats?.unitsSold ?? 0}
              prev={prevStats ? prevStats.unitsSold : null}
              fmt={fmtNum}
            />
            <Kpi
              label="Zákazníci"
              value={stats?.uniqueCustomerCount ?? 0}
              prev={prevStats ? prevStats.uniqueCustomerCount : null}
              fmt={fmtNum}
              subtitle={`${stats?.newCustomerCount ?? 0} nových`}
            />
            <Kpi
              label="Zaplatené"
              value={stats?.paidTotal ?? 0}
              prev={prevStats ? prevStats.paidTotal : null}
              valueColor="teal"
              subtitle={`${stats?.paidOrderCount ?? 0} obj.`}
            />
            <Kpi
              label="Nezaplatené"
              value={stats?.unpaidTotal ?? 0}
              prev={prevStats ? prevStats.unpaidTotal : null}
              goodUp={false}
              valueColor={(stats?.unpaidTotal ?? 0) > 0 ? 'orange' : undefined}
              subtitle={`${stats?.unpaidOrderCount ?? 0} obj.`}
            />
            <Kpi
              label="Vrátené"
              value={fin.refundsTotal}
              prev={prevStats ? prevFin.refundsTotal : null}
              goodUp={false}
            />
            <Kpi
              label="Náklady"
              value={expensesTotal}
              prev={prevStats ? prevExpensesTotal : null}
              goodUp={false}
            />
          </SimpleGrid>

          {/* Waterfall + Donut */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card withBorder radius="md" padding="md">
              <Text fw={600} mb="md">
                Z čoho sa skladá zisk
              </Text>
              <BarChart
                h={300}
                data={breakdownData}
                dataKey="step"
                series={[{ name: 'value', color: 'blue.6' }]}
                withLegend={false}
                gridAxis="xy"
                withBarValueLabel
                valueFormatter={(v) => formatCurrency(v)}
              />
            </Card>
            <Card withBorder radius="md" padding="md">
              <Text fw={600} mb="md">
                Platby podľa typu
              </Text>
              {donutData.length > 0 ? (
                <Group justify="center" align="center" gap="xl" wrap="wrap">
                  <DonutChart
                    h={220}
                    size={180}
                    thickness={28}
                    data={donutData}
                    withTooltip
                    valueFormatter={(v) => formatCurrency(v)}
                    chartLabel={formatCurrency(fin.grossInflow)}
                  />
                  <Stack gap={6}>
                    {donutData.map((d) => {
                      const pct = fin.grossInflow > 0 ? (d.value / fin.grossInflow) * 100 : 0;
                      return (
                        <Group key={d.name} gap="xs" wrap="nowrap">
                          <ColorSwatch color={swatch(d.color)} size={12} />
                          <Text size="sm">{d.name}</Text>
                          <Text size="sm" c="dimmed" ml="auto">
                            {formatCurrency(d.value)} · {pct.toFixed(0)} %
                          </Text>
                        </Group>
                      );
                    })}
                  </Stack>
                </Group>
              ) : (
                <Text size="sm" c="dimmed">
                  Žiadne dáta.
                </Text>
              )}
            </Card>
          </SimpleGrid>

          {/* Trend */}
          <Card withBorder radius="md" padding="md">
            <Text fw={600} mb="md">
              Tržby a hrubý zisk (odhad) podľa dní
            </Text>
            {trendData.length > 0 ? (
              <CompositeChart
                h={300}
                data={trendData}
                dataKey="date"
                series={[
                  { name: 'Tržby', color: 'blue.6', type: 'bar' },
                  { name: 'Zisk', color: 'teal.6', type: 'line' },
                ]}
                curveType="linear"
                withLegend
              />
            ) : (
              <Text size="sm" c="dimmed">
                Žiadne dáta v tomto rozsahu.
              </Text>
            )}
          </Card>

          {/* Payment method paid/unpaid table */}
          <Card withBorder radius="md" padding="md">
            <Text fw={600} mb="xs">
              Platby podľa typu — zaplatené / nezaplatené
            </Text>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Typ</Table.Th>
                  <Table.Th ta="right">Obj.</Table.Th>
                  <Table.Th ta="right">Spolu</Table.Th>
                  <Table.Th ta="right">Zaplatené</Table.Th>
                  <Table.Th ta="right">Nezaplatené</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(stats?.byPaymentMethod ?? []).map((p) => (
                  <Table.Tr key={p.method}>
                    <Table.Td>{p.method}</Table.Td>
                    <Table.Td ta="right">{p.orderCount}</Table.Td>
                    <Table.Td ta="right">{formatCurrency(p.total)}</Table.Td>
                    <Table.Td ta="right" c="teal">
                      {formatCurrency(p.paidTotal)} <Text component="span" size="xs" c="dimmed">({p.paidOrderCount})</Text>
                    </Table.Td>
                    <Table.Td ta="right" c={p.unpaidTotal > 0 ? 'orange' : 'dimmed'}>
                      {formatCurrency(p.unpaidTotal)} <Text component="span" size="xs" c="dimmed">({p.unpaidOrderCount})</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {(stats?.byPaymentMethod ?? []).length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5} c="dimmed" ta="center">
                      Žiadne dáta
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Card>

          {/* Per-live + top products */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card withBorder radius="md" padding="md">
              <Text fw={600} mb="xs">
                Tržby podľa live
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Live</Table.Th>
                    <Table.Th ta="right">Obj.</Table.Th>
                    <Table.Th ta="right">Tržby</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(stats?.byLive ?? []).map((l) => (
                    <Table.Tr key={l.liveId}>
                      <Table.Td>
                        {l.liveName}
                        {l.liveDate && (
                          <Text component="span" size="xs" c="dimmed">
                            {' '}
                            {dayjs(l.liveDate).format('DD.MM.')}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">{l.orderCount}</Table.Td>
                      <Table.Td ta="right">{formatCurrency(l.total)}</Table.Td>
                    </Table.Tr>
                  ))}
                  {(stats?.byLive ?? []).length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={3} c="dimmed" ta="center">
                        Žiadne dáta
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Card>
            <Card withBorder radius="md" padding="md">
              <Text fw={600} mb="xs">
                Najpredávanejšie produkty
              </Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Produkt</Table.Th>
                    <Table.Th ta="right">Ks</Table.Th>
                    <Table.Th ta="right">Tržby</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(stats?.topProducts ?? []).slice(0, 12).map((p) => (
                    <Table.Tr key={p.productId}>
                      <Table.Td>{p.productName}</Table.Td>
                      <Table.Td ta="right">{p.units}</Table.Td>
                      <Table.Td ta="right">{formatCurrency(p.total)}</Table.Td>
                    </Table.Tr>
                  ))}
                  {(stats?.topProducts ?? []).length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={3} c="dimmed" ta="center">
                        Žiadne dáta
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Card>
          </SimpleGrid>

          {/* Expenses editors */}
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Card withBorder radius="md" padding="md">
              <Group justify="space-between" mb="xs">
                <Group gap="xs">
                  <Text fw={600}>Pravidelné náklady</Text>
                  <Badge variant="light">mesačne</Badge>
                </Group>
                <ActionIcon
                  variant="light"
                  onClick={() =>
                    createExpense({ variables: { name: 'Nový náklad', amount: 0, type: 'REGULAR' } })
                  }
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>
              <Stack gap="xs">
                {regular.map((e) => (
                  <Group key={e.id} gap="xs" wrap="nowrap">
                    <TextInput
                      placeholder="Názov"
                      value={e.name}
                      onChange={(ev) => patchLocal(e.id, { name: ev.currentTarget.value })}
                      onBlur={() => updateExpense({ variables: { id: e.id, name: e.name } })}
                      style={{ flex: 1 }}
                    />
                    <NumberInput
                      value={e.amount}
                      onChange={(v) =>
                        patchLocal(e.id, { amount: typeof v === 'number' ? v : parseFloat(String(v)) || 0 })
                      }
                      onBlur={() => updateExpense({ variables: { id: e.id, amount: e.amount } })}
                      min={0}
                      suffix=" €"
                      w={120}
                    />
                    <ActionIcon variant="subtle" color="red" onClick={() => deleteExpense({ variables: { id: e.id } })}>
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Group>
                ))}
                {regular.length === 0 && (
                  <Text size="sm" c="dimmed">
                    Žiadne pravidelné náklady (mzdy, úvery, nájom…).
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  V rozsahu ({rangeDays} dní): {formatCurrency(fin.regularTotal)} z{' '}
                  {formatCurrency(regularMonthly)} / mesiac
                </Text>
              </Stack>
            </Card>
            <Card withBorder radius="md" padding="md">
              <Group justify="space-between" mb="xs">
                <Text fw={600}>Jednorazové náklady</Text>
                <ActionIcon
                  variant="light"
                  onClick={() =>
                    createExpense({
                      variables: { name: 'Nový náklad', amount: 0, type: 'ONE_OFF', date: toDateValue(new Date()) },
                    })
                  }
                >
                  <IconPlus size={16} />
                </ActionIcon>
              </Group>
              <Stack gap="xs">
                {oneOff.map((e) => {
                  const ds = e.date ? dayjs(e.date).format('YYYY-MM-DD') : null;
                  const inRange = ds ? ds >= fromStr && ds <= toStr : false;
                  return (
                    <Group key={e.id} gap="xs" wrap="nowrap">
                      <TextInput
                        placeholder="Názov"
                        value={e.name}
                        onChange={(ev) => patchLocal(e.id, { name: ev.currentTarget.value })}
                        onBlur={() => updateExpense({ variables: { id: e.id, name: e.name } })}
                        style={{ flex: 1 }}
                      />
                      <DatePickerInput
                        value={e.date ?? null}
                        onChange={(d) => {
                          const val = toDateValue(d);
                          patchLocal(e.id, { date: val });
                          updateExpense({ variables: { id: e.id, date: val } });
                        }}
                        valueFormat="DD.MM.YYYY"
                        w={130}
                        c={inRange ? undefined : 'dimmed'}
                      />
                      <NumberInput
                        value={e.amount}
                        onChange={(v) =>
                          patchLocal(e.id, { amount: typeof v === 'number' ? v : parseFloat(String(v)) || 0 })
                        }
                        onBlur={() => updateExpense({ variables: { id: e.id, amount: e.amount } })}
                        min={0}
                        suffix=" €"
                        w={110}
                      />
                      <ActionIcon variant="subtle" color="red" onClick={() => deleteExpense({ variables: { id: e.id } })}>
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  );
                })}
                {oneOff.length === 0 && (
                  <Text size="sm" c="dimmed">
                    Žiadne jednorazové náklady.
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  V rozsahu: {formatCurrency(fin.oneOffTotal)} (počíta sa len dátum v rozsahu)
                </Text>
              </Stack>
            </Card>
          </SimpleGrid>
        </Stack>
      )}
    </Container>
  );
}
